// 파일 업로드 공통 유틸 (작성/수정 폼에서 공유)

export const formatFileSize = (bytes) => {
  if (bytes == null) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/**
 * multipart FormData 생성.
 * @param {object} request  'request' 필드에 JSON blob 으로 담을 객체
 * @param {object} fileGroups  { 필드명: File[] } 형태의 파일 그룹들
 * @param {object} extra  추가로 append 할 [필드명]: 값(문자열/배열)
 */
export const buildFormData = (request, fileGroups = {}, extra = {}) => {
  const fd = new FormData();
  fd.append("request", new Blob([JSON.stringify(request)], { type: "application/json" }));

  Object.entries(fileGroups).forEach(([field, files]) => {
    (files || []).forEach((f) => {
      if (f && f.size > 0) fd.append(field, f);
    });
  });

  Object.entries(extra).forEach(([field, value]) => {
    if (Array.isArray(value)) {
      value.forEach((v) => fd.append(field, v));
    } else if (value != null) {
      fd.append(field, value);
    }
  });

  return fd;
};

/* --------------------------------------------------------------------------
   업로드 용량 가드
   서버 앞단 nginx 의 client_max_body_size 가 1MB 라, 그보다 큰 multipart 요청은
   백엔드에 닿지도 못하고 413 으로 끊긴다. 큰 사진을 그대로 올리다 실패하는 일이
   잦아 업로드 전에 브라우저에서 리사이즈·재압축한다.
   -------------------------------------------------------------------------- */

/** nginx 요청 본문 한도(1MB)에서 폼 필드 몫을 뺀 안전선. */
export const SAFE_TOTAL_BYTES = 950 * 1024;

const COMPRESSIBLE = /^image\/(jpeg|png|webp)$/i;

/**
 * 이미지를 긴 변 기준 maxDim 이하로 줄이고 targetBytes 이하가 될 때까지 JPEG 품질을 낮춘다.
 * 압축이 불가능하거나(예: GIF/SVG) 오히려 커지면 원본을 그대로 돌려준다.
 */
export const compressImage = async (file, { maxDim = 1600, targetBytes = 600 * 1024 } = {}) => {
  if (!file || !COMPRESSIBLE.test(file.type) || file.size <= 150 * 1024) return file;
  try {
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, maxDim / Math.max(bitmap.width, bitmap.height));
    const w = Math.max(1, Math.round(bitmap.width * scale));
    const h = Math.max(1, Math.round(bitmap.height * scale));

    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#ffffff";               // PNG 투명 영역이 검게 나오지 않도록
    ctx.fillRect(0, 0, w, h);
    ctx.drawImage(bitmap, 0, 0, w, h);
    if (bitmap.close) bitmap.close();

    let blob = null;
    for (const q of [0.86, 0.78, 0.7, 0.6, 0.5]) {
      // eslint-disable-next-line no-await-in-loop
      blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", q));
      if (blob && blob.size <= targetBytes) break;
    }
    if (!blob || blob.size >= file.size) return file;

    return new File([blob], `${file.name.replace(/\.[^.]+$/, "")}.jpg`, {
      type: "image/jpeg",
      lastModified: file.lastModified,
    });
  } catch {
    return file;                              // 압축 실패 시 원본 유지 (업로드 자체는 시도)
  }
};

export const totalSize = (files = []) => files.reduce((sum, f) => sum + ((f && f.size) || 0), 0);

export const isOverLimit = (files = []) => totalSize(files) > SAFE_TOTAL_BYTES;

/** 업로드 실패 사유를 사용자가 알아볼 수 있는 문구로 바꾼다. */
export const uploadErrorMessage = (err, fallback) => {
  if (err && err.response && err.response.status === 413) {
    return `이미지 용량이 서버 업로드 한도(1MB)를 초과했습니다.\n장수를 줄이거나 더 작은 이미지를 사용해 주세요.`;
  }
  return (err && err.response && err.response.data && err.response.data.message) || fallback;
};
